import { useMemo } from "react";
import { parseISO, format, differenceInDays, parse } from "date-fns";
import { useTranslation } from "react-i18next";

import { useViewNDVIContext } from "../ViewNDVIContext";
import { formatFixedNumber } from "../../../../../../shared/numberFormat";
import { buildFieldLandDownloadSubject } from "../../../../Fields/ViewField/ViewLand/Layers/downloadArchive";
import { getApexChartLocaleOptions, getNdviLineChartDisplayOptions } from "./apexChartLocale";
import RealDateLineChart from "./RealDateLineChart";

const TotalAvgHistory = () => {
  const { t } = useTranslation();
  const context = useViewNDVIContext();
  const chartConfig = useMemo(() => {
    const report = Object.assign({}, context.report);
    let dates = new Set();
    const lands = {};

    const reportDates = report.ndvi_land_dates;
    for (var z in reportDates) {
      const current = reportDates[z];
      if (current.usage != "USED") {
        continue;
      }

      let currentDate = null;
      if (current.date instanceof Date) {
        currentDate = current.date;
      } else {
        currentDate = parse(current.date, "yyyy-LL-dd'T'HH:mm:ss", new Date());
      }

      dates.add(format(currentDate, "yyyy-LL-dd"));

      if (!lands[current.land_id]) {
        lands[current.land_id] = [];
      }

      lands[current.land_id].push(current);
    }

    dates = [...dates]
    dates.sort()
    const dateTimestamps = dates.map(date => parse(date, "yyyy-LL-dd", new Date()).getTime())
    const totalSeries = []

    report.lands.forEach(land => {
      if (!lands[land.land_id]) {
        return;
      }

      const reportDates = lands[land.land_id].map(report => {
        const n = Object.assign({}, report);
        n["date"] = parseISO(report["date"]);
        return n;
      });

      const series = []
      dates.forEach(date => {
        const parsedDate = parse(date, "yyyy-LL-dd", new Date());
        let currentDiff = Number.MAX_VALUE;
        let choiced = null;

        reportDates.forEach(report => {
          const diff = Math.abs(differenceInDays(parsedDate, report.date));
          if (diff < currentDiff) {
            currentDiff = diff;
            choiced = report;
          }
        });

        if (choiced != null) {
          series.push({
            x: parsedDate.getTime(),
            y: choiced.avg_index,
          });
        }
      });

      if (series.length < 1) {
        return;
      }

      totalSeries.push({
        name: buildFieldLandDownloadSubject({
          fieldName: land.field?.name,
          landName: land.name,
        }),
        data: series
      });
    });

    const lineChartOptions = getNdviLineChartDisplayOptions({ dateTimestamps });

    return {
      dateTimestamps,
      options: {
        ...lineChartOptions,
        chart: {
          ...getApexChartLocaleOptions(t),
          id: "avg-history"
        },
        xaxis: {
          ...lineChartOptions.xaxis,
        },
        yaxis: {
          decimalsInFloat: 3,
          labels: {
            formatter: (value) => formatFixedNumber(value, 3),
          },
        },
        tooltip: {
          ...lineChartOptions.tooltip,
          y: {
            formatter: (value) => formatFixedNumber(value, 3),
          },
        },
      },
      series: totalSeries,
    };
  }, [context.report, t]);

  return (
    <>
      <RealDateLineChart
        dateTimestamps={chartConfig.dateTimestamps}
        options={chartConfig.options}
        series={chartConfig.series}
      />
    </>
  )
}

export default TotalAvgHistory;
